/**
 * HowLembraWorks, Sprint 2.5a A.0/B/S7b (Heidi scroll-PINNED pattern).
 *
 * Scroll-driven 4-phase shell that maps the chat-concierge product
 * end-to-end: Brief → Curate → Approve → Deliver. The section is
 * pinned (position: sticky on the inner content) and the user's
 * scroll position drives which phase is active. Scrolling down
 * advances; scrolling up reverses. The same frame/header/tab
 * shell stays put while the mockup + body text swap.
 *
 * S3 was a click-driven first cut. S7b is the actual scroll-pinned
 * Heidi pattern Lucky asked for.
 *
 * Implementation:
 *   • Outer section gets explicit height: 400vh on desktop (75vh
 *     of scroll per phase) and 320vh on mobile (80vh per phase,
 *     less total swiping)
 *   • Inner content is position: sticky, top: 0, height: 100vh,
 *     stays anchored in the viewport while the section scrolls
 *     past it
 *   • A single scroll listener (rAF-throttled, DOM-write-only via
 *     setState) reads the section's getBoundingClientRect and maps
 *     scroll progress to phase index
 *   • Tab clicks scroll-jump to the appropriate position within
 *     the section's pin range (so clicks still feel like they
 *     work, but the underlying source of truth is scroll position)
 *   • prefers-reduced-motion: scroll-jump uses behavior:'instant'
 *     so the user isn't dragged through smooth animation
 *
 * Pioneer alignment:
 * - Norman: scroll IS the natural advance signal, the section
 *   self-explains by responding to the user's main input device
 * - Krug: the user doesn't have to click anything to move through;
 *   the page already moves through it for them
 * - Wroblewski: mobile gets a slightly compressed pin so finger
 *   scrolling doesn't feel infinite
 * - Stanley: tabs still work via click + keyboard; scroll-jump
 *   target is computed deterministically so a tabbing user lands
 *   on the right phase
 */

import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useIsMobile } from "@/hooks/use-mobile";
// MS368: mobile gets its own phone-frame component rather than
// sharing the desktop chrome. See HowLembraWorksMobile.tsx.
import { HowLembraWorksMobile } from "@/components/lembra/HowLembraWorksMobile";
import {
  Cake,
  Check,
  Coffee,
  Gift,
  Leaf,
  MessageSquare,
  Package,
  Send,
  Sparkles,
  Star,
  type LucideIcon,
} from "lucide-react";
// MS374: borrowed from the mobile HowLembraWorks component to power the
// animated chat thread inside Brief's right panel. CelebrationGlyphMorph
// + LEMBRA_VERB_VOCAB.conciergeRecommend are the same primitives the
// live chat uses for its "thinking" state, so the mockup looks like
// the real product.
import { CelebrationGlyphMorph } from "@/components/lembra/CelebrationGlyph";
import { LEMBRA_VERB_VOCAB } from "@/components/lembra/ThinkingStrip";

const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const displayFamily = "Newsreader, Georgia, serif";
const sansFamily = "DM Sans, sans-serif";

interface Phase {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Tab-row label for the active state (typically same as label). */
  title: string;
  /** Body line shown beneath the frame when this phase is active. */
  body: string;
  /** The mockup rendered inside the gradient frame. */
  mockup: ReactNode;
}

/* ─── Mockup primitives ──────────────────────────────────────────
   Inline JSX so we don't depend on real screenshots. Each mockup
   is a stylised representation of the actual product UI a user
   would see in that phase, recognisable, not photorealistic.
   Built with our existing palette so they read as Lembra UI, not
   as generic chrome. */

function MockupChrome({ children }: { children: ReactNode }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl"
      style={{
        // MS24/265/357/362/367/374/375 history preserved in git.
        // Current surface: cream app background, navy hairline border,
        // soft shadow. MS374 dropped the browser-chrome top strip
        // (3 dots + tinted bar). MS375 bumped TOP padding from
        // 14-16px → 22-26px so the eyebrow text in Curate / Approve /
        // Deliver isn't crammed against the frame edge — it now sits
        // ~10px lower with visible breathing room above it. Sides +
        // bottom unchanged so card content still uses the chrome's
        // full horizontal width and the bottom CTA / preview row
        // hugs the floor as before. Brief is unaffected since its
        // inner panels carry their own padding rhythm.
        background: "#FAF6F0",
        padding:
          "clamp(22px, 4.8vw, 26px) clamp(14px, 4vw, 16px) clamp(14px, 4vw, 16px)",
        height: "var(--mockup-chrome-height, clamp(380px, 52vh, 440px))",
        border: "1px solid rgba(29,52,97,0.08)",
        boxShadow:
          "0 1px 2px rgba(29,52,97,0.04), 0 24px 60px -30px rgba(29,52,97,0.4)",
      }}
    >
      <div className="relative h-full">{children}</div>
    </div>
  );
}

/* ─── Phase 1: Brief (MS266 split-screen rewrite) ─────────────────
   Two-panel layout per Lucky's "way too simplistic" call on the
   chat-only original:
   - Left panel: dashboard preview ("Upcoming this month" list +
     autopilot pill + 3 celebration rows). Shows the buyer that
     beyond one-off briefs, Lembra holds the calendar + can autopilot
     entire celebrations. The first impression of the product surface.
   - Right panel: condensed chat (user brief + assistant reply +
     three-dot typing indicator). Same beats as the old chat-only
     mockup, narrower column.
   The two-column structure is intentional — it makes Brief visually
   distinct from Curate/Approve/Deliver (which are single-panel)
   without breaking the consistent chrome rhythm. The split reinforces
   "your dashboard AND a concierge", which is the real product story
   for Phase 1.
   Width math: at the new 836w chrome, ~26px padding leaves 810w.
   Grid-cols-2 + gap-3 = each panel ~395px. Fits 1280w+ comfortably;
   collapses to single column at mobile via the responsive class.
*/
function BriefMockup() {
  // MS374/375: verb-cycle for the AI "thinking" state inside the chat
  // bubble — same primitive the live chat uses (ThinkingStrip).
  // MS375 sped this up after Lucky's "takes too long" call: cycle
  // every 480ms for the first ~1400ms (was 700ms × 2400ms). At ~3
  // verbs visible the AI still reads as "thinking through it", but
  // the whole user → AI → resolved beat now completes in ~2s instead
  // of ~3.3s. Resolved content takes over via CSS at 1400ms.
  const verbs = LEMBRA_VERB_VOCAB.conciergeRecommend;
  const reducedMotion = useReducedMotion();
  const [verbIdx, setVerbIdx] = useState(0);
  useEffect(() => {
    if (reducedMotion) return;
    const t = setInterval(() => setVerbIdx((i) => (i + 1) % verbs.length), 480);
    const stop = setTimeout(() => clearInterval(t), 1400);
    return () => {
      clearInterval(t);
      clearTimeout(stop);
    };
  }, [reducedMotion, verbs.length]);

  // MS374: Sarah is the through-line across all 4 phases now. Her row
  // is HIGHLIGHTED (coral border + light coral tint) and her occasion
  // reads "Birthday · vegan · coffee" to mirror the chat brief on
  // the right panel exactly. Tom + Lila stay as supporting "team
  // calendar feels alive" texture.
  const upcoming = [
    {
      initials: "SC",
      avatarBg: "#C4564A",
      name: "Sarah Chen",
      occasion: "Birthday · vegan · coffee",
      date: "Fri 22 Aug",
      auto: true,
      isFocus: true,
    },
    {
      initials: "TB",
      avatarBg: "#1D3461",
      name: "Tom Bell",
      occasion: "Work anniversary · 5 yrs",
      date: "Mon 25 Aug",
      auto: false,
      isFocus: false,
    },
    {
      initials: "LM",
      avatarBg: "#F7E8A4",
      avatarText: "#1D3461",
      name: "Lila Marcos",
      occasion: "Welcome",
      date: "Wed 27 Aug",
      auto: true,
      isFocus: false,
    },
  ] as const;

  return (
    <MockupChrome>
      {/* MS357: mobile collapses to dashboard-only. The original layout
          stacked dashboard + chat panel vertically on mobile, which
          compressed each panel to ~165px and clipped the chat bubble's
          third message. Dashboard alone tells the "calendar +
          autopilot" story cleanly; the chat thread shows up in the
          Curate / Approve mockups that come next in the carousel. */}
      <div className="grid h-full gap-3 md:grid-cols-2">
        {/* LEFT — dashboard preview */}
        <div
          className="flex flex-col rounded-xl bg-white p-3"
          style={{
            border: "1px solid rgba(29,52,97,0.08)",
            boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
          }}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <p
              className="text-[10px] uppercase tracking-[0.16em] text-navy/60"
              style={{ fontFamily: monoFamily }}
            >
              Upcoming · this month
            </p>
            <span
              className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider"
              style={{
                background: "rgba(196,86,74,0.12)",
                color: "#C4564A",
                fontFamily: monoFamily,
              }}
            >
              <Sparkles size={9} strokeWidth={2.2} />
              Autopilot
            </span>
          </div>

          {/* MS366: bumped every text + control by ~1 step on mobile
              (responsive classes) so the dashboard rows read at the
              same scale as Deliver's notification cards — Lucky's
              "pills are larger, texts are larger, but not zoomed in"
              criterion. Desktop sizes unchanged via md: prefix on
              the original values. */}
          <div className="flex flex-1 flex-col gap-2">
            {upcoming.map((row) => (
              <div
                key={row.name}
                className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 md:px-2 md:py-1.5"
                style={{
                  // MS374: Sarah's row highlighted to mirror the chat
                  // on the right panel — same recipient, visible
                  // confirmation that the brief lives in the dashboard.
                  background: row.isFocus
                    ? "rgba(196,86,74,0.06)"
                    : "rgba(45,41,38,0.025)",
                  border: row.isFocus
                    ? "1px solid rgba(196,86,74,0.20)"
                    : "1px solid rgba(29,52,97,0.05)",
                }}
              >
                <span
                  className="inline-flex h-9 w-9 md:h-8 md:w-8 shrink-0 items-center justify-center rounded-full text-[12px] md:text-[11px] font-semibold"
                  style={{
                    background: row.avatarBg,
                    color: "avatarText" in row ? row.avatarText : "white",
                    fontFamily: sansFamily,
                  }}
                >
                  {row.initials}
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className="truncate text-[14px] md:text-[12px] font-semibold leading-tight text-navy"
                    style={{ fontFamily: sansFamily }}
                  >
                    {row.name}
                  </p>
                  <p
                    className="truncate text-[12px] md:text-[10px] text-navy/55"
                    style={{ fontFamily: sansFamily }}
                  >
                    {row.occasion}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  <p
                    className="text-[12px] md:text-[10px] text-navy/65"
                    style={{ fontFamily: monoFamily, letterSpacing: "0.02em" }}
                  >
                    {row.date}
                  </p>
                  {row.auto ? (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] md:text-[8.5px] uppercase tracking-wider text-coral"
                      style={{ fontFamily: monoFamily }}
                    >
                      <Check size={8} strokeWidth={2.5} /> Auto
                    </span>
                  ) : (
                    <span
                      className="text-[10px] md:text-[8.5px] uppercase tracking-wider text-navy/45"
                      style={{ fontFamily: monoFamily }}
                    >
                      Awaiting you
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — animated chat thread (MS374/375 port from mobile).
            MS375 restructured to read like a real chat surface per
            Lucky's call:
              - Eyebrow shortened from "Brief · live chat" → just
                "Live chat" (the dashboard left of it already establishes
                this IS the brief surface).
              - Messages now ANCHOR TO THE BOTTOM of the panel via a
                flex-1 + justify-end inner column, matching iMessage /
                WhatsApp / actual Lembra chat. Was top-anchored which
                made the conversation float in mid-panel with white
                space below.
              - A composer row (placeholder text + coral send disc)
                sits at the very bottom of the panel so the buyer
                sees the actual input affordance, not just transcript.
              - Animation timings tightened (200→150ms user, 900→500ms
                AI, 2400→1400ms resolve) so the full sequence completes
                in ~2s instead of ~3.3s. */}
        <div
          className="flex flex-col rounded-xl bg-white p-3"
          style={{
            border: "1px solid rgba(29,52,97,0.08)",
            boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
          }}
        >
          <p
            className="mb-0.5 text-[10px] uppercase tracking-[0.16em] text-navy/60"
            style={{ fontFamily: monoFamily }}
          >
            Live chat
          </p>

          {/* Message stack — pinned to the BOTTOM of the panel via
              flex-1 + justify-end. Empty whitespace lives at the top
              the way it does in any real chat surface (messages grow
              up from the composer, not down from the eyebrow). */}
          <div className="flex flex-1 flex-col justify-end gap-2">
            {/* User bubble — slides up from below */}
            <div
              className="flex w-full justify-end"
              style={{
                animation: "lembraBriefUserIn 460ms cubic-bezier(0.16, 1, 0.3, 1) both",
                animationDelay: "150ms",
              }}
            >
              <div
                className="max-w-[90%] rounded-2xl rounded-br-md px-3 py-2 text-[12.5px] leading-snug"
                style={{ background: "#C4564A", color: "white", fontFamily: sansFamily }}
              >
                Sarah&apos;s birthday next Friday. She&apos;s vegan and loves coffee.
              </div>
            </div>
            {/* AI bubble — slides in at 500ms, content morphs at 1400ms.
                Grid-stack so the bubble height stays constant from
                thinking → resolved (no layout shift). */}
            <div
              className="flex w-full justify-start"
              style={{
                animation: "lembraBriefAiIn 460ms cubic-bezier(0.16, 1, 0.3, 1) both",
                animationDelay: "500ms",
              }}
            >
              <div className="flex max-w-[92%] flex-col items-start gap-1">
                <p
                  className="ml-1 text-[9px] uppercase text-charcoal/55"
                  style={{ fontFamily: sansFamily, letterSpacing: "0.08em" }}
                >
                  Lembra
                </p>
                <div
                  className="inline-flex rounded-2xl rounded-bl-md px-3 py-2"
                  style={{
                    background: "rgba(29,52,97,0.06)",
                    fontFamily: sansFamily,
                  }}
                >
                  <div className="grid">
                    {/* Thinking content visible 0–1400ms, then fades */}
                    <div
                      className="flex items-center gap-2"
                      style={{
                        gridArea: "1 / 1",
                        animation:
                          "lembraBriefAiThinkingOut 320ms cubic-bezier(0.16, 1, 0.3, 1) 1400ms forwards",
                      }}
                    >
                      <CelebrationGlyphMorph size={14} colorClass="text-navy/75" />
                      <span
                        key={verbIdx}
                        className="text-[12px] text-navy/75 whitespace-nowrap"
                        style={{ animation: "lembraVerbFade 280ms ease-out" }}
                      >
                        {verbs[verbIdx]}
                      </span>
                    </div>
                    {/* Resolved content fades in at 1400ms in the same
                        grid cell — bubble height stays constant. */}
                    <div
                      className="flex items-start gap-1.5 text-[12.5px] leading-snug text-navy"
                      style={{
                        gridArea: "1 / 1",
                        opacity: 0,
                        animation:
                          "lembraBriefResolvedIn 320ms cubic-bezier(0.16, 1, 0.3, 1) 1400ms forwards",
                      }}
                    >
                      <Sparkles
                        size={11}
                        strokeWidth={2.2}
                        className="text-coral shrink-0 mt-0.5"
                      />
                      <span>Got it. Three picks coming up for Sarah.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Composer — sits at the very bottom of the panel like a
              real chat composer. Placeholder text + coral send disc.
              tabIndex/aria-hidden so screen readers + keyboard users
              don't try to interact with a non-functional mockup. */}
          <div
            className="mt-2 flex items-center gap-2 rounded-full px-3 py-1.5"
            style={{
              background: "rgba(29,52,97,0.04)",
              border: "1px solid rgba(29,52,97,0.08)",
            }}
            aria-hidden="true"
          >
            <span
              className="flex-1 text-[11px] text-navy/40 truncate"
              style={{ fontFamily: sansFamily }}
            >
              Type a message…
            </span>
            <span
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
              style={{ background: "#C4564A" }}
            >
              <Send size={11} className="text-white" strokeWidth={2.4} />
            </span>
          </div>
        </div>

        <style>{`
          @keyframes lembraBriefUserIn {
            from { opacity: 0; transform: translateY(10px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes lembraBriefAiIn {
            from { opacity: 0; transform: translateY(10px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes lembraBriefAiThinkingOut {
            from { opacity: 1; }
            to   { opacity: 0; }
          }
          @keyframes lembraBriefResolvedIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes lembraVerbFade {
            from { opacity: 0; transform: translateY(2px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @media (prefers-reduced-motion: reduce) {
            @keyframes lembraBriefUserIn { 0%, 100% { opacity: 1; transform: none; } }
            @keyframes lembraBriefAiIn { 0%, 100% { opacity: 1; transform: none; } }
            @keyframes lembraBriefAiThinkingOut { 0%, 100% { opacity: 0; } }
            @keyframes lembraBriefResolvedIn { 0%, 100% { opacity: 1; } }
            @keyframes lembraVerbFade { 0%, 100% { opacity: 1; } }
          }
        `}</style>
      </div>
    </MockupChrome>
  );
}

/* ─── Phase 2: Curate ────────────────────────────────────────────
   Three considered picks. Each card shows vendor + product + price
   AND a "match" reasoning chip explaining WHY this matches Sarah's
   brief, that's the considered-not-random signal. Cake card uses
   a real Le Yeahllow photo (already on the Shopify CDN); the
   other two use stylised gradient swatches with a category icon.
   Mixing photo + stylised is intentional, it's how the real
   surfacing screen would actually read across categories. */
function CurateMockup() {
  // MS374: same 3-vendor trio the mobile preview uses, so Brief →
  // Curate carousel reads as one continuous story (Sarah, vegan +
  // coffee → these 3 picks). Vendor names in casual-case for the
  // brand label (was all-caps shouting). Reasoning chips name the
  // SPECIFIC brief-attribute matched ("Coffee fit", "Her local",
  // "Vegan-safe") instead of generic "On theme".
  const cards = [
    {
      vendor: "Maker & Monger",
      title: "Cheese hamper",
      priceMeta: "$82 · cheese + beans + biscotti",
      reasoning: "Coffee fit",
      reasoningIcon: Coffee,
      image: "/vendor-photos/maker-and-monger/hampers1.webp",
      imageAlt: "Cheese and charcuterie hamper from Maker & Monger",
    },
    {
      vendor: "Patricia Coffee",
      title: "$80 voucher",
      priceMeta: "Single origin · 1 year",
      reasoning: "Her local",
      reasoningIcon: Sparkles,
      image: "/vendor-photos/patricia-coffee/picnic-trio.jpg",
      imageAlt: "Patricia Coffee Brewers voucher",
    },
    {
      vendor: "Larkspur Flowers",
      title: "Native bouquet",
      priceMeta: "$68 · locally grown",
      reasoning: "Vegan-safe",
      reasoningIcon: Sparkles,
      image: "/vendor-photos/larkspur/flowers1.jpg",
      imageAlt: "Native flower bouquet from Larkspur",
    },
  ] as const;

  return (
    <MockupChrome>
      <div className="flex h-full flex-col">
        {/* MS265: mb-3 → mb-2 + larger eyebrow (10→11px) so it reads
            with the brand voice instead of feeling like microcopy. */}
        <div className="mb-2 flex items-center gap-2">
          <span
            className="h-6 w-6 shrink-0 rounded-full flex items-center justify-center"
            style={{ background: "#1D3461" }}
          >
            <Sparkles size={11} className="text-butter" strokeWidth={2.2} />
          </span>
          <p
            className="text-[11px] uppercase tracking-[0.18em] text-navy/60"
            style={{ fontFamily: monoFamily }}
          >
            Three considered picks, for Sarah
          </p>
        </div>

        {/* MS366: restored the full three-card carousel on mobile per
            Lucky's call. The original MS359 hide-2nd-and-3rd fix was
            in response to the chrome appearing to extend past the
            right edge — but the section now has overflow-x: hidden
            (MS359 same-PR defensive belt) so the horizontal scroll-
            snap carousel can return without leaking page-level
            horizontal scroll.
            Mobile carousel: each card is 78% of chrome width, snap-
            mandatory so users land on one card at a time. The
            `-mx-3 px-3` lets cards bleed to the chrome edge while
            keeping the first-card snap origin aligned. Desktop keeps
            the 3-up grid (no scroll). */}
        <div className="grid flex-1 gap-3 grid-cols-1 md:grid-cols-3 max-md:flex max-md:overflow-x-auto max-md:snap-x max-md:snap-mandatory max-md:-mx-3 max-md:px-3 max-md:[&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
          {cards.map((c) => {
            const ReasonIcon = c.reasoningIcon;
            return (
              <div
                key={c.title}
                className="flex flex-col overflow-hidden rounded-xl bg-white max-md:snap-center max-md:shrink-0 max-md:w-[78%]"
                style={{
                  border: "1px solid rgba(29,52,97,0.1)",
                  boxShadow: "0 4px 10px -4px rgba(29,52,97,0.12)",
                }}
              >
                {/* MS265: aspect-ratio 4/3 → 16/9. Frees ~48px vertical
                    per card so the larger body text below fits without
                    pushing the card past the chrome ceiling. */}
                <div
                  className="w-full overflow-hidden"
                  style={{
                    aspectRatio: "16 / 9",
                    background: "rgba(45,41,38,0.04)",
                  }}
                >
                  {/* MS362: mobile uses object-contain so the full
                      cake photo shows with letterbox space inside the
                      card image area, rather than the cover-crop that
                      read as "too zoomed in" per Lucky's audit. Desktop
                      stays on cover where the 3-up grid + larger cards
                      have room for the cropped composition to feel
                      intentional. */}
                  <img
                    src={c.image}
                    alt={c.imageAlt}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                    className="object-contain md:object-cover"
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      background: "rgba(45,41,38,0.04)",
                    }}
                  />
                </div>
                {/* MS265: card body — bumped every type size up one
                    step (vendor 8→10, title 12→14, price 10→12, chip
                    8→10) per Lucky's "everything's too small" call.
                    Padding p-2.5 → p-3 for breathing room around the
                    larger text. Added "View →" CTA at bottom-right so
                    each card has a visible call to action. */}
                <div className="flex flex-1 flex-col p-3">
                  <p
                    className="text-[10px] uppercase tracking-[0.14em] text-navy/60"
                    style={{ fontFamily: monoFamily }}
                  >
                    {c.vendor}
                  </p>
                  <p
                    className="mt-1 text-[14px] font-semibold leading-tight text-navy"
                    style={{ fontFamily: sansFamily }}
                  >
                    {c.title}
                  </p>
                  <p
                    className="mt-0.5 text-[12px] text-navy/65"
                    style={{ fontFamily: sansFamily }}
                  >
                    {c.priceMeta}
                  </p>
                  <div className="mt-auto flex items-center justify-between gap-2 pt-2">
                    <div
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider"
                      style={{
                        background: "rgba(196,86,74,0.10)",
                        color: "#C4564A",
                        fontFamily: monoFamily,
                      }}
                    >
                      <ReasonIcon size={10} strokeWidth={2} />
                      {c.reasoning}
                    </div>
                    <span
                      className="text-[11px] font-medium text-coral"
                      style={{ fontFamily: sansFamily }}
                    >
                      View →
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </MockupChrome>
  );
}

/* ─── Phase 3: Approve ───────────────────────────────────────────
   Two-column layout: chosen card on the left (with a card-message
   preview baked in), three "behind-the-scenes" checklist items on
   the right with a small reassurance line at the bottom. Conveys
   the atomic "one tap, everything happens" nature of confirming. */
function ApproveMockup() {
  // MS374: copy aligned to Sarah's vegan-birthday brief from Phase 1.
  // Courier date matches her dashboard row ("Fri 22 Aug").
  const checklist = [
    {
      title: "Card signed",
      detail: "In your name. Hand-printed on cream stock.",
    },
    {
      title: "Vendor briefed",
      detail: "Maker & Monger has Sarah's vegan dietary on file.",
    },
    {
      title: "Courier booked",
      detail: "Fri 22 Aug · 2 PM window · Sarah's desk.",
    },
  ];
  return (
    <MockupChrome>
      {/* MS11: tightened gap-3 → gap-2, image h-16 → h-14, p-4 → p-3
          on chosen card, checklist gap-2.5 → gap-2, card-message
          padding p-2 → px-2 py-1.5. Em-dash in card message
          replaced with comma. Saves ~28px and fits all viewports. */}
      <div className="flex h-full flex-col gap-2">
        <div className="flex items-center gap-2">
          <Check size={12} className="text-coral" strokeWidth={2.5} />
          <p
            className="text-[10px] uppercase tracking-[0.18em] text-navy/55"
            style={{ fontFamily: monoFamily }}
          >
            Sarah's gift, confirmed
          </p>
        </div>

        <div className="grid flex-1 gap-3 grid-cols-1 md:grid-cols-2">
          {/* MS24: chosen card swapped from gradient-coffee mockup to a
              real Cremorne hamper photo. Card message preview now sits
              on white with a navy/8 border for proper contrast (was
              cream-on-cream — Lucky's "hard to read" call). */}
          <div
            className="flex flex-col overflow-hidden rounded-xl bg-white"
            style={{
              border: "2px solid #C4564A",
              boxShadow: "0 8px 24px -8px rgba(196,86,74,0.35)",
            }}
          >
            {/* MS265: aspect 16:9 → 21:9. The left card's image was
                pushing total card height to 314px which overflowed
                the chrome's inner 279px (at 720h) by 35px. A wider
                21:9 crop reads as a band of imagery without dominating
                the card vertically; saves ~53px.
                MS362: mobile uses object-contain so the full hamper
                photo shows (bottles, jars, the spread) rather than the
                21:9 cover-crop slice that read as "zoomed in / can't
                tell what it is" per Lucky's audit. Desktop keeps the
                cover-crop for the tighter editorial band — the
                aspectRatio wrapper holds the layout slot either way. */}
            <div
              className="w-full overflow-hidden"
              style={{ aspectRatio: "21 / 9", background: "rgba(45,41,38,0.04)" }}
            >
              <img
                src="/vendor-photos/maker-and-monger/hampers1.webp"
                alt="Cheese hamper from Maker & Monger"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
                className="object-contain md:object-cover"
                style={{ width: "100%", height: "100%", display: "block", background: "rgba(45,41,38,0.04)" }}
              />
            </div>
            <div className="flex flex-1 flex-col p-3">
              {/* MS374: continuity with Brief/Curate — Sarah's pick
                  is the Maker & Monger cheese hamper from Curate's
                  trio. Vendor name in casual case to match the cards. */}
              <p
                className="text-[10px] uppercase tracking-[0.14em] text-navy/55"
                style={{ fontFamily: monoFamily }}
              >
                Maker &amp; Monger
              </p>
              <p
                className="mt-0.5 text-[13px] font-semibold leading-snug text-navy"
                style={{ fontFamily: sansFamily }}
              >
                Cheese hamper
              </p>
              <p
                className="mt-0.5 text-[11px] text-navy/60"
                style={{ fontFamily: sansFamily }}
              >
                $82 · cheese + beans + biscotti
              </p>
              <div
                className="mt-auto pt-2 rounded-md px-2 py-1"
                style={{
                  background: "white",
                  border: "1px solid rgba(29,52,97,0.1)",
                }}
              >
                <p
                  className="text-[11px] leading-snug text-navy"
                  style={{ fontFamily: displayFamily, fontStyle: "italic" }}
                >
                  Three years of being you. Happy birthday, Sarah.
                </p>
              </div>
            </div>
          </div>

          {/* Behind-the-scenes checklist — MS265: bumped every type
              size (title 12→14, detail 10→12, reassurance 11→13) +
              icon avatar 5→6 + icon 11→13. Was reading as microcopy
              floating in white space; now matches the chosen-card
              type weight on the left and uses the column's width.
              MS366: hidden-on-mobile rule from MS359 reverted per
              Lucky's "approve preview is no longer the beautiful
              version" call. Both columns now render on mobile,
              stacking via the parent grid (grid-cols-1 md:grid-
              cols-2) — chosen card on top, checklist below. The
              chrome's 380px floor (MS357) gives ~180px per stacked
              panel which fits both cleanly. */}
          <div
            className="flex flex-col gap-3 rounded-xl bg-white p-3.5"
            style={{ border: "1px solid rgba(29,52,97,0.08)" }}
          >
            {/* MS374: swap coral check tiles → green (#2D695A) so the
                "done" beats visually separate from the coral border on
                the chosen card + the coral CTA below. Three-coral-on-
                coral was monotonous. Mirrors the mobile checklist
                colour palette. */}
            {checklist.map((item) => (
              <div key={item.title} className="flex items-start gap-2.5">
                <span
                  className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                  style={{ background: "rgba(45,105,90,0.12)" }}
                >
                  <Check size={13} style={{ color: "#2D695A" }} strokeWidth={2.5} />
                </span>
                <div>
                  <p
                    className="text-[14px] font-semibold leading-tight text-navy"
                    style={{ fontFamily: sansFamily }}
                  >
                    {item.title}
                  </p>
                  <p
                    className="mt-0.5 text-[12px] leading-snug text-navy/65"
                    style={{ fontFamily: sansFamily }}
                  >
                    {item.detail}
                  </p>
                </div>
              </div>
            ))}
            {/* MS374: dual CTA replacing the static reassurance pill.
                "Edit" outlined navy = secondary (refine before send);
                "Send to Sarah" coral = primary action with a pulse
                animation. Buyer sees the actual decision they'd make,
                not a generic reassurance line. */}
            <div className="mt-auto flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                className="inline-flex items-center rounded-full border px-3 py-1.5 text-[12px] font-semibold text-navy"
                style={{
                  borderColor: "rgba(29,52,97,0.18)",
                  background: "white",
                  fontFamily: sansFamily,
                }}
                tabIndex={-1}
                aria-hidden="true"
              >
                Edit
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold text-white"
                style={{
                  background: "#C4564A",
                  fontFamily: sansFamily,
                  boxShadow: "0 6px 16px -6px rgba(196,86,74,0.5)",
                  animation: "lembraSendPulse 1800ms ease-in-out infinite",
                }}
                tabIndex={-1}
                aria-hidden="true"
              >
                Send to Sarah
                <Send size={11} strokeWidth={2.4} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </MockupChrome>
  );
}

/* ─── Phase 4: Deliver ───────────────────────────────────────────
   A vertical timeline showing the day-of journey: vendor confirmed
   → out for delivery → delivered, with a card-message preview
   inside the delivered step. The "delivered" step is the visual
   anchor (coral dot + bg accent) since that's the final beat the
   user actually cares about. Reinforces the "you didn't lift a
   finger" message via the timeline format, these things happened
   on their own. */
function DeliverMockup() {
  // MS376: two-column split mirroring Brief's pattern (dashboard left
  // / live activity right). Closes two issues at once: kills the
  // empty horizontal space the single-column timeline left, AND
  // surfaces the registered-dashboard pitch (Sarah lives in the
  // platform as a persistent profile that compounds over time, not
  // as a one-off concierge transaction).
  //
  // FIELD HONESTY — every field rendered in the left panel maps 1:1
  // to a column that already exists on the `team_members` table and
  // is already rendered by src/pages/TeamMemberProfile.tsx today:
  //   - name + department          → `name` + `department`
  //   - birthday + tenure          → `birthday` + `start_date` (yrs
  //                                  derived in UI)
  //   - Vegan chip                 → `dietary_requirements` text[]
  //                                  (canonical `vegan` per
  //                                  src/lib/dietaryLabels.ts)
  //   - "Coffee · single origin"   → `favourite_foods` text[]
  //   - "No flowers" chip          → `gift_preferences.avoid` jsonb
  //                                  (reframed from the original
  //                                  "Plants over flowers" — the
  //                                  schema only persists the avoid,
  //                                  so the chip honours that)
  //   - Recent gifts list          → `orders` joined by
  //                                  `team_member_name` (the existing
  //                                  MemberHistorySection query in
  //                                  TeamMemberProfile.tsx lines
  //                                  192–209 powers this in the live
  //                                  dashboard)
  //
  // RIGHT panel keeps the 3-card notification stack from MS374/375
  // (vendor confirmed → out for delivery → delivered) minus the prior
  // bottom card-message preview row, which had no schema backing
  // (`orders` has no `card_message` column today).
  const steps = [
    {
      title: "Vendor confirmed",
      time: "2h ago",
      body: "Maker & Monger has Sarah's hamper and the card.",
      icon: Check,
      isHighlight: false,
    },
    {
      title: "Out for delivery",
      time: "47m ago",
      body: "Courier picked up. ETA to Sarah's desk 2:30 PM.",
      icon: Package,
      isHighlight: false,
    },
    {
      title: "Delivered to Sarah's desk",
      time: "now",
      body: "2 minutes early. Recipient signed for it.",
      icon: Check,
      isHighlight: true,
    },
  ];

  return (
    <MockupChrome>
      <div className="grid h-full gap-3 md:grid-cols-2">
        {/* LEFT — Sarah's profile snippet (real schema fields) */}
        <div
          className="flex flex-col rounded-xl bg-white p-3"
          style={{
            border: "1px solid rgba(29,52,97,0.08)",
            boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
          }}
        >
          <p
            className="mb-2 text-[10px] uppercase tracking-[0.16em] text-navy/60"
            style={{ fontFamily: monoFamily }}
          >
            Profile · Sarah Chen
          </p>

          {/* Header row — avatar + name + department */}
          <div
            className="mb-2 flex items-center gap-2 pb-2"
            style={{ borderBottom: "1px solid rgba(29,52,97,0.06)" }}
          >
            <span
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold"
              style={{ background: "#C4564A", color: "white", fontFamily: sansFamily }}
            >
              SC
            </span>
            <div className="flex-1 min-w-0">
              <p
                className="text-[13px] font-semibold leading-tight text-navy"
                style={{ fontFamily: sansFamily }}
              >
                Sarah Chen
              </p>
              <p
                className="text-[10px] text-navy/55"
                style={{ fontFamily: sansFamily }}
              >
                Engineering
              </p>
            </div>
          </div>

          {/* Key dates */}
          <div className="mb-2">
            <p
              className="mb-1 text-[9px] uppercase tracking-[0.14em] text-navy/45"
              style={{ fontFamily: monoFamily }}
            >
              Key dates
            </p>
            <div className="flex flex-col gap-1">
              <div
                className="flex items-center justify-between text-[11px]"
                style={{ fontFamily: sansFamily }}
              >
                <span className="flex items-center gap-1.5 text-navy/70">
                  <Cake size={11} strokeWidth={2.2} className="text-coral" />
                  Birthday
                </span>
                <span className="font-medium text-navy/85">Fri 22 Aug</span>
              </div>
              <div
                className="flex items-center justify-between text-[11px]"
                style={{ fontFamily: sansFamily }}
              >
                <span className="flex items-center gap-1.5 text-navy/70">
                  <Star size={11} strokeWidth={2.2} className="text-coral" />
                  Joined
                </span>
                <span className="font-medium text-navy/85">23 Mar · 3 yrs</span>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="mb-2">
            <p
              className="mb-1 text-[9px] uppercase tracking-[0.14em] text-navy/45"
              style={{ fontFamily: monoFamily }}
            >
              Preferences
            </p>
            <div className="flex flex-wrap gap-1">
              <span
                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
                style={{
                  background: "rgba(45,105,90,0.10)",
                  color: "#2D695A",
                  fontFamily: monoFamily,
                }}
              >
                <Leaf size={9} strokeWidth={2.2} />
                Vegan
              </span>
              <span
                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
                style={{
                  background: "rgba(196,86,74,0.10)",
                  color: "#C4564A",
                  fontFamily: monoFamily,
                }}
              >
                <Coffee size={9} strokeWidth={2.2} />
                Coffee · single origin
              </span>
              <span
                className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px]"
                style={{
                  background: "rgba(29,52,97,0.06)",
                  color: "rgba(29,52,97,0.65)",
                  fontFamily: monoFamily,
                }}
              >
                Avoid · flowers
              </span>
            </div>
          </div>

          {/* Recent gifts — flex-1 so it stretches to fill remaining
              vertical space when chrome is taller; recent gifts is
              the natural "bottom of the panel" beat anyway. */}
          <div className="flex flex-1 flex-col">
            <p
              className="mb-1 text-[9px] uppercase tracking-[0.14em] text-navy/45"
              style={{ fontFamily: monoFamily }}
            >
              Recent gifts
            </p>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-start gap-2">
                <Gift
                  size={11}
                  strokeWidth={2.2}
                  className="mt-0.5 shrink-0 text-coral"
                />
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[11px] font-medium leading-tight text-navy"
                    style={{ fontFamily: sansFamily }}
                  >
                    Cheese hamper · today
                  </p>
                  <p
                    className="text-[10px] leading-tight text-navy/55"
                    style={{ fontFamily: sansFamily }}
                  >
                    Maker &amp; Monger
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Gift
                  size={11}
                  strokeWidth={2.2}
                  className="mt-0.5 shrink-0 text-navy/40"
                />
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[11px] font-medium leading-tight text-navy"
                    style={{ fontFamily: sansFamily }}
                  >
                    Coffee voucher · 25 Mar
                  </p>
                  <p
                    className="text-[10px] leading-tight text-navy/55"
                    style={{ fontFamily: sansFamily }}
                  >
                    Patricia Coffee
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — live notifications (existing stack, refit for
            half-width column). MS376 dropped maxWidth: 560 wrapper
            since column width already constrains. Card-message
            preview row dropped — orders has no card_message column
            today, so the italic message was fiction. */}
        <div
          className="flex flex-col rounded-xl bg-white p-3"
          style={{
            border: "1px solid rgba(29,52,97,0.08)",
            boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
          }}
        >
          <p
            className="mb-2 text-[10px] uppercase tracking-[0.16em] text-navy/60"
            style={{ fontFamily: monoFamily }}
          >
            Live updates
          </p>

          <div className="flex flex-1 flex-col gap-1.5">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="flex items-start gap-2 rounded-xl bg-white px-2.5 py-2"
                  style={{
                    border: step.isHighlight
                      ? "1px solid rgba(196,86,74,0.3)"
                      : "1px solid rgba(29,52,97,0.08)",
                    boxShadow: step.isHighlight
                      ? "0 4px 12px -4px rgba(196,86,74,0.18)"
                      : "0 1px 2px rgba(29,52,97,0.04)",
                    animation: `lembraNotifSlideIn 480ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 140}ms both`,
                  }}
                >
                  <span
                    className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                    style={{
                      background: step.isHighlight ? "#C4564A" : "rgba(45,41,38,0.05)",
                    }}
                  >
                    <Icon
                      size={13}
                      strokeWidth={2.4}
                      className={step.isHighlight ? "text-white" : "text-navy/70"}
                    />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className="text-[12px] font-semibold leading-tight text-navy"
                        style={{ fontFamily: sansFamily }}
                      >
                        {step.title}
                      </p>
                      {step.isHighlight ? (
                        <span
                          className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider"
                          style={{
                            background: "rgba(196,86,74,0.12)",
                            color: "#C4564A",
                            fontFamily: monoFamily,
                          }}
                        >
                          {step.time}
                        </span>
                      ) : (
                        <p
                          className="shrink-0 text-[10px] text-navy/55"
                          style={{ fontFamily: monoFamily, letterSpacing: "0.02em" }}
                        >
                          {step.time}
                        </p>
                      )}
                    </div>
                    <p
                      className="mt-0.5 text-[10.5px] leading-snug text-navy/65"
                      style={{ fontFamily: sansFamily }}
                    >
                      {step.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </MockupChrome>
  );
}

const PHASES: Phase[] = [
  {
    id: "brief",
    label: "Brief",
    icon: MessageSquare,
    title: "Brief the concierge",
    body:
      "Tell Lembra who's celebrating. Name, what's happening, anything we should know. Hobbies, dietaries, their style. A sentence is enough; we'll ask if we need more.",
    mockup: <BriefMockup />,
  },
  {
    id: "curate",
    label: "Curate",
    icon: Sparkles,
    title: "We surface a handful of options",
    body:
      "Lembra picks from local vendors (cakes, hampers, flowers, vouchers) sized for the budget and the moment. You see three considered choices, not a catalogue to wade through.",
    mockup: <CurateMockup />,
  },
  {
    id: "approve",
    label: "Approve",
    icon: Check,
    title: "Pick the one that feels right",
    body:
      "Click to confirm. The card is already signed, the vendor is already briefed, the courier is already booked. One tap and it's locked in.",
    mockup: <ApproveMockup />,
  },
  {
    id: "deliver",
    label: "Deliver",
    icon: Package,
    title: "It lands quietly on their desk",
    body:
      "On the day, a courier from the chosen vendor delivers in person. No announcement, no staged moment. You get a confirmation. They get a gift that feels considered.",
    mockup: <DeliverMockup />,
  },
];

interface HowLembraWorksProps {
  id?: string;
}

// MS368: parent component switches on viewport — mobile gets a
// purpose-built phone-frame mockup (HowLembraWorksMobile), desktop
// keeps the proven 400vh sticky scroll-pin layout below. The two
// surfaces now own their own presentation end-to-end and can evolve
// independently — no more cascade where every mobile fix breaks the
// desktop sticky pin math.
export function HowLembraWorks({ id }: HowLembraWorksProps) {
  // useIsMobile is the canonical hook — returns false on first SSR
  // render then settles via useEffect/matchMedia in the browser.
  // We mount HowLembraWorksDesktop (the existing component) on
  // desktop and HowLembraWorksMobile on mobile. Both render under
  // the same id anchor so navigation links keep working unchanged.
  const isMobile = useIsMobile();
  if (isMobile) {
    return <HowLembraWorksMobile id={id} />;
  }
  return <HowLembraWorksDesktop id={id} />;
}

function HowLembraWorksDesktop({ id }: HowLembraWorksProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768,
  );
  // MS184: shared useReducedMotion hook replaces the inline matchMedia
  // check inside jumpToPhase. Lifting it to the component body means
  // jumpToPhase becomes a one-liner (`scrollTo({ behavior: reducedMotion
  // ? "instant" : "smooth" })`), and the callback's useCallback memo
  // now correctly invalidates when the OS pref toggles.
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);

  // Track viewport size. Mobile gets a flat, click-driven layout
  // (no sticky, no scroll-driven phase progression) per Lucky's MS11
  // call, touch users were getting trapped in an effectively
  // infinite scroll surface that felt broken.
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Mobile auto-advance: loop through phases on a timer so the
  // section reads as a self-playing carousel. Desktop is scroll-
  // driven (handled below) and doesn't need this.
  useEffect(() => {
    if (!isMobile || reducedMotion) return;
    const interval = setInterval(() => {
      setActiveIdx((idx) => (idx + 1) % PHASES.length);
    }, 3600);
    return () => clearInterval(interval);
  }, [isMobile, reducedMotion]);

  // MS372: with the sticky pin actually working again (overflow-x:
  // clip fix), the section can go back to a length that gives the
  // user comfortable scroll range to advance through 4 phases at
  // natural wheel speed. 220vh = 120vh of pin scroll = 30vh per
  // phase (~3 wheel notches each). The user's perception of "long
  // navy" was driven by the pin BEING BROKEN — section scrolled
  // past as a slab instead of pinning. With pin working it feels
  // held in place + phases advance under the cursor instead of
  // sliding by.
  const sectionVh = isMobile ? null : 220;

  // ── Scroll-driven phase progression (DESKTOP ONLY) ───────────
  // Continuous rAF poll that reads the section's bounding rect every
  // frame and maps scroll progress to a phase index.
  useEffect(() => {
    if (isMobile) return;
    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    let rafId: number | null = null;
    let lastIdx = -1;

    const tick = () => {
      const rect = sectionEl.getBoundingClientRect();
      const vh = window.innerHeight;

      let idx: number;
      if (rect.top >= vh) {
        idx = 0;
      } else if (rect.bottom <= 0) {
        idx = PHASES.length - 1;
      } else if (rect.top > 0) {
        idx = 0;
      } else if (rect.bottom < vh) {
        idx = PHASES.length - 1;
      } else {
        const scrolledIntoPin = -rect.top;
        const totalPinScroll = rect.height - vh;
        const progress = totalPinScroll > 0
          ? Math.max(0, Math.min(1, scrolledIntoPin / totalPinScroll))
          : 0;
        idx = Math.min(
          PHASES.length - 1,
          Math.floor(progress * PHASES.length),
        );
      }

      if (idx !== lastIdx) {
        lastIdx = idx;
        setActiveIdx(idx);
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isMobile]);

  // ── Tab click handler ────────────────────────────────────────
  // Desktop: scroll-jumps to the phase's pin position so the rAF tick
  // picks it up. Mobile: just sets state directly.
  const jumpToPhase = useCallback(
    (idx: number) => {
      if (isMobile) {
        setActiveIdx(idx);
        return;
      }
      const sectionEl = sectionRef.current;
      if (!sectionEl) return;
      const rect = sectionEl.getBoundingClientRect();
      const sectionTopAbs = rect.top + window.scrollY;
      const totalPinScroll = rect.height - window.innerHeight;
      const phaseTargetProgress = (idx + 0.5) / PHASES.length;
      const targetScrollY = sectionTopAbs + totalPinScroll * phaseTargetProgress;
      window.scrollTo({
        top: targetScrollY,
        behavior: reducedMotion ? "instant" : "smooth",
      });
    },
    [isMobile, reducedMotion],
  );

  const active = PHASES[activeIdx];

  return (
    <section
      ref={sectionRef}
      id={id}
      // MS361: scroll-margin bumped from 80px → 96px so the eyebrow
      // ("HOW LEMBRA WORKS") + headline sit clear of the fixed top
      // nav when a visitor clicks the "How it works" anchor in the
      // navigation pill. The nav is ~64px tall + we want a 32px
      // visual gap below it.
      className="scroll-mt-[96px] relative"
      style={{
        position: "relative",
        zIndex: 3,
        background: "#1D3461",
        color: "#FAF6F0",
        // MS372: was `overflow-x: hidden` (MS359 defensive belt against
        // the mobile Curate carousel leaking past the viewport edge).
        // `overflow-x: hidden` silently broke desktop sticky-pinning
        // since the day it shipped — `overflow: hidden` on any axis
        // turns the element into a scroll container, which prevents
        // `position: sticky` children from pinning to the viewport.
        // This was Lucky's "pin doesn't engage" bug: the rAF tick
        // fired but the sticky inner was never actually pinned, so
        // phases scrubbed as the section scrolled past instead of
        // while it stayed in place.
        //
        // `overflow-x: clip` does the same clipping job without
        // establishing a scroll container, so sticky positioning
        // continues to work. Safari 16+, Chrome 90+, Firefox 81+ —
        // baseline browsers we already require.
        overflowX: "clip",
        // Desktop: section is taller than the viewport so the inner
        // sticky child can pin and scroll-driven progression works.
        // Mobile: auto-height (no sticky, just stacked content with
        // tap-to-switch tabs). MS11.
        height: sectionVh ? `${sectionVh}vh` : "auto",
      }}
    >
      {/* MS370: sticky inner restored on desktop so the scroll-pin
          works. Mobile stays as a plain block (HowLembraWorksMobile
          handles its own layout). */}
      <div
        className={isMobile ? "" : "sticky"}
        style={{
          top: isMobile ? undefined : 0,
          minHeight: isMobile ? undefined : "100dvh",
          display: "flex",
          alignItems: "center",
          // MS369: desktop top padding bumped back up to add breathing
          // room above the header. Was clamp(32,3.5vw,56) — felt
          // crammed against the eyebrow on Lucky's audit. Now
          // clamp(56,5vw,88) — at 1440w that's 72px (was 50px), at
          // 1920w that's 88px (was 56). Bottom padding kept tight
          // since the closer CTA already gives the bottom edge
          // visual breathing room. Mobile branch unchanged.
          padding: isMobile
            ? "clamp(32px, 6vw, 48px) 0"
            : "clamp(56px, 5vw, 88px) 0 clamp(16px, 1.4vw, 24px)",
          background: "#1D3461",
        }}
      >
      {/* Decorative radial glows, same butter-top-right + sky-
          bottom-left treatment as before. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 600px 280px at 80% 0%, rgba(247,232,164,0.06), transparent 60%), radial-gradient(ellipse 480px 320px at 10% 100%, rgba(168,197,226,0.05), transparent 60%)",
        }}
      />

      {/* S8a: maxWidth tightened 1080 → 900. Lucky's call: Heidi's
          full-bleed feature shell doesn't suit us; the contained
          editorial frame reads better here. */}
      <div className="relative w-full px-6 md:px-8" style={{ maxWidth: 900, margin: "0 auto" }}>
        {/* Header, stays put, only the H2 emphasis flips per phase.
            MS369: bottom margin 8 → 16 for desktop breathing room. */}
        <header className="text-center" style={{ maxWidth: 720, margin: "0 auto 16px" }}>
          <span className="inline-flex items-center gap-2.5">
            <span
              aria-hidden="true"
              style={{ display: "inline-block", width: 22, height: 1, background: "#F7E8A4" }}
            />
            <span
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#F7E8A4",
              }}
            >
              How Lembra works
            </span>
            <span
              aria-hidden="true"
              style={{ display: "inline-block", width: 22, height: 1, background: "#F7E8A4" }}
            />
          </span>
          <h2
            style={{
              fontFamily: displayFamily,
              fontStyle: "italic",
              fontWeight: 500,
              // MS369: opened back up for desktop breathing. Was
              // clamp(22,2.6vw,30); now clamp(26,2.9vw,34) — at
              // 1440w it's 34px (was 30px), still falls back to
              // 26px at narrow widths.
              fontSize: "clamp(26px, 2.9vw, 34px)",
              lineHeight: 1.1,
              letterSpacing: "-0.022em",
              color: "#FAF6F0",
              margin: "10px 0 8px",
            }}
          >
            Tell us about them.{" "}
            <span style={{ color: "#F7E8A4" }}>We do the rest.</span>
          </h2>
          {/* MS362: subhead hidden on mobile per Lucky's call. The
              H2 ("Tell us about them. We do the rest.") already
              carries the section premise; the four-steps subhead
              was adding a copy beat the mobile viewport doesn't
              have room for. Desktop keeps it for the rhythm. */}
          {!isMobile && (
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 14,
                lineHeight: 1.5,
                color: "rgba(250,246,240,0.66)",
                margin: "0 auto",
                maxWidth: "56ch",
              }}
            >
              Four steps from brief to desk drop.
            </p>
          )}
        </header>

        {/* Frame, true crossfade between mockups. All 4 mockups
            stack in a single CSS Grid cell (grid-row: 1 / grid-column: 1
            on each child), so the wrapper sizes to the TALLEST child
            automatically. MS11: replaces the prior position:absolute
            scheme that left taller mockups (Approve / Curate) leaking
            below the wrapper into the tab row on certain viewports.
            Grid-stack is the cleanest "overlay everything, size to
            tallest" pattern, no minHeight safety net needed, no
            absolute-positioning offsets to maintain. */}
        <div
          aria-live="polite"
          style={{ display: "grid" }}
        >
          {PHASES.map((p, i) => {
            const isActive = i === activeIdx;
            return (
              <div
                key={p.id}
                aria-hidden={!isActive}
                style={{
                  gridRow: "1 / 2",
                  gridColumn: "1 / 2",
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? "translateY(0)" : "translateY(6px)",
                  transition:
                    "opacity 520ms cubic-bezier(0.16, 1, 0.3, 1), transform 520ms cubic-bezier(0.16, 1, 0.3, 1)",
                  pointerEvents: isActive ? "auto" : "none",
                }}
              >
                {p.mockup}
              </div>
            );
          })}
        </div>

        {/* Pill tab row + body. No hover-pause needed now, scroll IS
            the source of truth, so hovering the tabs just makes them
            click-targets without changing state.
            MS265: mt-3 → mt-2 + gap-2 → gap-1.5 to tighten the stack.
            MS362: mobile gets extra breathing room around the pill —
            mt-5 (instead of mt-2) so the pill clears the mockup chrome
            visually, and gap-4 (instead of gap-1.5) so the H3 title
            below the pill has air to read. Pre-MS362 the pill was
            crammed flush against both the chrome and the headline,
            making the section feel congested on phones. Desktop keeps
            the tight mt-2 gap-1.5 since there's more vertical real
            estate. */}
        <div className={isMobile ? "mt-5 flex flex-col items-center gap-4" : "mt-5 flex flex-col items-center gap-6"}>
          {isMobile ? (
            // Mobile: ONE pill that morphs to match the active phase
            // as the carousel above auto-loops. Replaces the prior
            // four-pill row which crammed the section vertically.
            // The pill's icon + label crossfade so the change reads
            // as an intentional state, not a flicker.
            // MS360: dropped the "1 / 4 · " step-counter prefix per
            // Lucky's call — visitors don't need to be told this is
            // step N of M, the label alone ("Brief" / "Curate" /
            // "Approve" / "Deliver") reads cleaner and matches the
            // desktop pill row which never had numbering either.
            <div
              role="tablist"
              aria-label="Lembra concierge phases"
              className="flex items-center justify-center"
            >
              <button
                key={active.id}
                type="button"
                role="tab"
                aria-selected
                aria-controls={`phase-panel-${active.id}`}
                onClick={() => jumpToPhase((activeIdx + 1) % PHASES.length)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12px] font-medium whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-butter"
                style={{
                  fontFamily: sansFamily,
                  background: "#1D3461",
                  color: "#F7E8A4",
                  border: "1px solid rgba(247,232,164,0.45)",
                  boxShadow:
                    "0 4px 14px -6px rgba(247,232,164,0.20), 0 0 0 3px rgba(247,232,164,0.06)",
                  transition: "all 320ms cubic-bezier(0.16, 1, 0.3, 1)",
                  animation: "phaseTextFadeIn 360ms cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              >
                <active.icon size={12} strokeWidth={2.2} />
                <span style={{ fontStyle: "italic" }}>
                  {active.label}
                </span>
              </button>
            </div>
          ) : (
            <div
              role="tablist"
              aria-label="Lembra concierge phases"
              className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full p-1.5"
              style={{
                border: "1px solid rgba(247,232,164,0.18)",
                background: "rgba(247,232,164,0.04)",
              }}
            >
              {PHASES.map((p, i) => {
                const Icon = p.icon;
                const isActive = i === activeIdx;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`phase-panel-${p.id}`}
                    onClick={() => jumpToPhase(i)}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-butter"
                    style={{
                      fontFamily: sansFamily,
                      background: isActive ? "#1D3461" : "transparent",
                      color: isActive ? "#F7E8A4" : "rgba(250,246,240,0.55)",
                      border: isActive
                        ? "1px solid rgba(247,232,164,0.45)"
                        : "1px solid transparent",
                      boxShadow: isActive
                        ? "0 4px 14px -6px rgba(247,232,164,0.20), 0 0 0 3px rgba(247,232,164,0.06)"
                        : "none",
                      transition:
                        "background-color 320ms cubic-bezier(0.16, 1, 0.3, 1), color 320ms cubic-bezier(0.16, 1, 0.3, 1), border-color 320ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 320ms cubic-bezier(0.16, 1, 0.3, 1)",
                    }}
                  >
                    <Icon size={13} strokeWidth={2.2} />
                    <span style={{ fontStyle: isActive ? "italic" : "normal" }}>
                      {p.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Phase title + body, same grid-stack crossfade pattern
              as the mockup above. */}
          <div
            id={`phase-panel-${active.id}`}
            role="tabpanel"
            aria-labelledby={`phase-tab-${active.id}`}
            className="text-center"
            style={{ display: "grid" }}
          >
            {PHASES.map((p, i) => {
              const isActive = i === activeIdx;
              return (
                <div
                  key={p.id}
                  aria-hidden={!isActive}
                  style={{
                    gridRow: "1 / 2",
                    gridColumn: "1 / 2",
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? "translateY(0)" : "translateY(4px)",
                    transition:
                      "opacity 460ms cubic-bezier(0.16, 1, 0.3, 1), transform 460ms cubic-bezier(0.16, 1, 0.3, 1)",
                    pointerEvents: isActive ? "auto" : "none",
                  }}
                >
                  <h3
                    style={{
                      fontFamily: displayFamily,
                      fontStyle: "italic",
                      fontWeight: 500,
                      // MS265: shrunk to claw back tabpanel height at
                      // short viewports. Was clamp(1.15,1.9vw,1.5rem)
                      // = 18.4–24px; now 16–21.6px.
                      fontSize: "clamp(1rem, 1.6vw, 1.35rem)",
                      lineHeight: 1.18,
                      color: "#FAF6F0",
                      margin: "0 auto 4px",
                      letterSpacing: "-0.01em",
                      maxWidth: "30ch",
                    }}
                  >
                    {p.title}
                  </h3>
                  {/* MS362: per-phase body paragraph hidden on
                      mobile per Lucky's call. The H3 title ("Brief
                      the concierge" / "We surface a handful of
                      options" / etc.) reads cleanly on its own; the
                      body was adding microcopy that crammed the
                      vertical space below the mockup. Desktop keeps
                      it for the rhythm + the substantiating detail. */}
                  {!isMobile && (
                    <p
                      style={{
                        fontFamily: sansFamily,
                        fontSize: 13,
                        lineHeight: 1.45,
                        color: "rgba(250,246,240,0.7)",
                        margin: "0 auto",
                        maxWidth: "56ch",
                      }}
                    >
                      {p.body}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Closer CTA, kept inside the sticky wrapper so it's
            visible at every phase. The user can brief immediately
            without waiting to scroll past the pin.
            MS265: mt-3 → mt-2 for tighter vertical rhythm.
            MS366: copy "Brief the concierge →" → "Send a gift →"
            per Lucky's call. Brief language was internal-product
            jargon; "Send a gift" is what the buyer is actually
            doing and matches the nav's "Send" CTA. */}
        <div className="mt-5 flex justify-center">
          <Link
            to="/chat"
            className="inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
            style={{ fontFamily: sansFamily }}
          >
            Send a gift →
          </Link>
        </div>
      </div>
      </div>{/* end sticky wrapper */}

      <style>{`
        @keyframes phaseFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes phaseTextFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        /* MS374: shared with the Approve mockup's "Send to Sarah" CTA
           and Deliver's notification stack. Pulse mimics the live
           product's send-button glow at rest; slide-in gives Deliver's
           notif stack arrival rhythm. Both keyframes are defined at
           the section root so any mockup can reference them without
           remounting a per-component <style> block. */
        @keyframes lembraSendPulse {
          0%, 100% { box-shadow: 0 8px 22px -6px rgba(196,86,74,0.45), 0 0 0 0 rgba(196,86,74,0.4); }
          50%      { box-shadow: 0 8px 22px -6px rgba(196,86,74,0.45), 0 0 0 8px rgba(196,86,74,0); }
        }
        @keyframes lembraNotifSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="lembraSendPulse"],
          [style*="lembraNotifSlideIn"] {
            animation: none !important;
          }
        }
        /* Mobile: taller chrome so the inner mockups (Curate's now-
           horizontal card carousel, Brief's stacked split panels,
           Deliver's three notification cards) have real room to
           render at readable type sizes instead of being squashed
           into a 220-300px ceiling that cropped imagery. */
        @media (max-width: 767px) {
          #how-it-works { --mockup-chrome-height: clamp(340px, 56vh, 460px); }
        }
      `}</style>
    </section>
  );
}
